import React, { useEffect, memo, useState } from 'react'
import { motion } from 'framer-motion'

const LoadingAnimation = memo(({ onFinish }: { onFinish?: () => void }) => {
  const [phase, setPhase] = useState<'loading' | 'transition'>('loading')
  const [progress, setProgress] = useState(0)
  
  // Efeito de progresso simulado
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval)
          return 100
        }
        // Incremento aleatório para parecer processamento real
        return Math.min(prev + Math.random() * 2, 100)
      })
    }, 50)
    
    // Timeline da transição
    const loadingDuration = 3500
    const t1 = setTimeout(() => setPhase('transition'), loadingDuration)
    const t2 = setTimeout(() => {
        if (onFinish) onFinish()
    }, loadingDuration + 800)
    
    return () => {
        clearInterval(interval)
        clearTimeout(t1)
        clearTimeout(t2)
    }
  }, [onFinish])

  return (
    <div className="fixed inset-0 bg-[#050b14] z-[9999] overflow-hidden flex flex-col items-center justify-center font-mono">
      
      {/* --- BACKGROUND --- */}
      {/* Grid sutil no fundo */}
      <div 
        className="absolute inset-0 z-0 opacity-10"
        style={{
            backgroundImage: `linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
        }}
      />
      
      {/* Scanlines Horizontais (CRT Effect) */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none opacity-30"
        style={{
          background: 'linear-gradient(to bottom, rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06))',
          backgroundSize: '100% 4px, 6px 100%'
        }}
      />
      
      {/* Vignette para focar no centro */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, transparent 40%, rgba(5, 11, 20, 0.8) 90%)'
        }}
      />

      {/* --- CONTENT --- */}
      <div className="relative z-20 flex flex-col items-center justify-center w-full max-w-lg px-8 gap-8">
        
        {/* LOGO: DOIS CÍRCULOS (OLHOS) */}
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={phase === 'loading' ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.1, filter: 'blur(10px)' }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-8 mb-4"
        >
            {/* Olho Esquerdo */}
            <motion.div 
                animate={{ scaleY: [1, 1, 1, 0.1, 1, 1, 1] }}
                transition={{ 
                    duration: 4, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    times: [0, 0.9, 0.95, 0.96, 0.97, 0.99, 1] // Blink randomico
                }}
                className="relative w-24 h-24 md:w-32 md:h-32 rounded-full border-[6px] border-cyan-400 shadow-[0_0_50px_rgba(6,182,212,0.8),inset_0_0_20px_rgba(6,182,212,0.4)] bg-black/40 flex items-center justify-center overflow-hidden"
            >
                {/* Reflexo interno */}
                <div className="absolute inset-0 rounded-full border-t-2 border-l-2 border-cyan-100/50" />
                
                {/* Íris Pulsante Central */}
                <motion.div 
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="w-16 h-16 rounded-full border-2 border-cyan-300/30 shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                />
            </motion.div>

            {/* Olho Direito (Pisca mais frequente - Wink) */}
            <motion.div 
                animate={{ scaleY: [1, 1, 0.1, 1, 1] }}
                transition={{ 
                    duration: 5, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    times: [0, 0.8, 0.82, 0.84, 1],
                    delay: 1 // Descompasso
                }}
                className="relative w-24 h-24 md:w-32 md:h-32 rounded-full border-[6px] border-cyan-400 shadow-[0_0_50px_rgba(6,182,212,0.8),inset_0_0_20px_rgba(6,182,212,0.4)] bg-black/40 flex items-center justify-center overflow-hidden"
            >
                 {/* Reflexo interno */}
                 <div className="absolute inset-0 rounded-full border-t-2 border-l-2 border-cyan-100/50" />
                 
                 {/* Íris Pulsante Central */}
                <motion.div 
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                    className="w-16 h-16 rounded-full border-2 border-cyan-300/30 shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                />
            </motion.div>
        </motion.div>

        {/* TEXTO: OVERPIXEL */}
        <motion.h1 
            initial={{ opacity: 0, letterSpacing: '0.2em' }}
            animate={phase === 'loading' ? { opacity: 1, letterSpacing: '0.5em' } : { opacity: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-cyan-500 tracking-[0.5em] uppercase text-center"
            style={{ 
                textShadow: '0 0 20px rgba(6,182,212,0.8), 0 0 10px rgba(6,182,212,0.5)'
            }}
        >
            OVERPIXEL
        </motion.h1>

        {/* LOADING BAR SECTION */}
        <motion.div 
          className="w-full mt-12"
          initial={{ opacity: 0 }}
          animate={phase === 'loading' ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {/* Labels acima da barra */}
          <div className="flex justify-between items-end mb-3 text-sm md:text-base text-cyan-500/70 font-bold tracking-widest font-mono">
            <span className="animate-pulse">INICIALIZANDO...</span>
            <span className="text-cyan-500/50">READY</span>
          </div>
          
          {/* Barra de Progresso */}
          <div className="h-1.5 w-full bg-cyan-900/30 relative">
            {/* Bar Fill */}
            <motion.div 
              className="h-full bg-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.8)] relative"
              style={{ width: `${progress}%` }}
            >
              {/* Cabeça da barra (brilho leading edge) */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-24 h-4 bg-gradient-to-l from-cyan-200 to-transparent opacity-50 blur-[2px]" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
})

export default LoadingAnimation
